import React, { FC, useId } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'urql';

import ComposeMetaAssignees from '../../../components/Compose/Common/ComposeMeta/ComposeMetaAssignees';
import Assignment from '../../../components/MUI/Compose/Assignment';
import ComposeSection from '../../../components/MUI/Compose/ComposeSection';
import {
  TopicAssignableDocument,
  User,
  UserGroup,
} from '../../../generated/graphql';
import translate from '../../../i18n/translate';
import { ComposeModeParam } from '../../../models/pageParams';

import intlPluralFormatter from '../../../utils/intlPluralFormat';
import userGetName from '../../../utils/user/userGetName';
import useComposeLinking from '../hooks/useComposeLinking';

import { mapDispatchToProps, mapStateToProps } from './redux';
import { Props } from './types';

/**
 * Assignment for groups and members
 *
 * @param props                           Props passed to the container
 * @param props.dataAssigneeSearchValue   Current search value
 * @param props.dataGroupsAssigned        Groups currently checked
 * @param props.dataGroupsSaved           Groups saved
 * @param props.dataMembersAssigned       Members currently checked
 * @param props.dataMembersSaved          Members saved
 * @param props.debugLoading              Whether to permanently enable loading state
 * @param props.nodeType                  Whether the compose form is being used for messages or templates
 * @param props.requestAssigneeSearch     Request update to search term
 * @param props.requestAssignGroupUpdate  Request to (un)check a group
 * @param props.requestAssignMemberUpdate Request to (un)check a member
 * @param props.requestAssignSave         Request to store currently selected items to saved
 * @param props.topicId                   The ID of the topic we're composing for
 * @returns                               The container itself
 */
const ComposeMetaAssignmentContainer: FC<Props> = ({
  dataAssigneeSearchValue,
  dataGroupsAssigned,
  dataGroupsSaved,
  dataMembersAssigned,
  dataMembersSaved,
  debugLoading,
  nodeType,
  requestAssigneeSearch,
  requestAssignGroupUpdate,
  requestAssignMemberUpdate,
  requestAssignSave,
  topicId,
}) => {
  const [{ data: queryData, fetching }] = useQuery({
    pause: topicId === null,
    query: TopicAssignableDocument,
    variables: { topicId: topicId ?? '' },
  });

  const data = topicId === null ? null : queryData;

  const idHeading = useId();
  const textHeading = translate('ASSIGNMENT__EDIT__PLACEHOLDER');

  const mode: ComposeModeParam = 'assign';
  const { isActive, linkToggleProps } = useComposeLinking(mode);

  const { topicAssignableGroups, topicAssignableMembers } = data ?? {
    topicAssignableGroups: [],
    topicAssignableMembers: [],
  };

  const groups = topicAssignableGroups.filter(({ id }) =>
    dataGroupsSaved.has(id),
  );
  const users = topicAssignableMembers.filter(({ id }) =>
    dataMembersSaved.has(id),
  );

  const namesAssignees = users.map(member => userGetName(member));
  const namesGroups = groups.map(group => group.title);
  const namesSaved = [...namesAssignees, ...namesGroups];

  const isLoading = debugLoading || fetching;

  /**
   * Change assign group state
   *
   * @param groupId User group id
   * @param state   Whether assign or unassign user group
   */
  const requestAssignGroupChange = (
    groupId: UserGroup['id'],
    state: boolean,
  ) => {
    requestAssignGroupUpdate(groupId, state);
    requestAssignSave();
  };

  /**
   * Change assign member state
   *
   * @param userId User id
   * @param state  Whether assign or unassign user
   */
  const requestAssignMemberChange = (userId: User['id'], state: boolean) => {
    requestAssignMemberUpdate(userId, state);
    requestAssignSave();
  };

  return (
    <ComposeSection
      idHeading={idHeading}
      inputCustom={<ComposeMetaAssignees groups={groups} users={users} />}
      isActive={isActive}
      linkToggleProps={linkToggleProps}
      textHeading={textHeading}
      value={intlPluralFormatter(namesSaved)}
    >
      <Assignment
        dataGroupsAll={topicAssignableGroups}
        dataGroupsAssigned={dataGroupsAssigned}
        dataMembersAll={topicAssignableMembers}
        dataMembersAssigned={dataMembersAssigned}
        dataSearchValue={dataAssigneeSearchValue}
        isLoading={isLoading}
        nodeType={nodeType}
        onAssignUpdateGroup={requestAssignGroupChange}
        onAssignUpdateMember={requestAssignMemberChange}
        onSearch={requestAssigneeSearch}
      />
    </ComposeSection>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ComposeMetaAssignmentContainer);
